<template>
  <div class="tw-space-y-8">
    <!-- Loading state -->
    <div v-if="isLoading" class="tw-space-y-8">
      <div class="tw-animate-pulse">
        <div class="tw-h-8 tw-bg-gray-200 tw-rounded tw-w-1/3 tw-mb-4"></div>
        <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-6 tw-mb-8">
          <div class="tw-h-32 tw-bg-gray-200 tw-rounded"></div>
          <div class="tw-h-32 tw-bg-gray-200 tw-rounded"></div>
          <div class="tw-h-32 tw-bg-gray-200 tw-rounded"></div>
        </div>
        <div class="tw-h-64 tw-bg-gray-200 tw-rounded tw-mb-8"></div>
        <div class="tw-h-48 tw-bg-gray-200 tw-rounded tw-mb-8"></div>
        <div class="tw-h-48 tw-bg-gray-200 tw-rounded"></div>
      </div>
    </div>

    <div v-else class="tw-space-y-8">
      <div className="tw-flex tw-items-center tw-justify-between">
        <div className="tw-flex tw-items-center tw-gap-3">
          <!-- Customer Avatar -->
          <div class="tw-relative" v-if="receivedData.avatar">
            <img
              :src="receivedData.avatar"
              :alt="receivedData.name || 'Customer'"
              class="tw-w-12 tw-h-12 tw-rounded-full tw-object-cover tw-border-2 tw-border-gray-200"
            />
          </div>

          <!-- Customer Status -->
          <Badge
            variant="custom"
            :class="{
              'tw-bg-green-100 tw-text-green-600 tw-border tw-border-green-600':
                receivedData.is_active,
              'tw-bg-red-100 tw-text-red-600 tw-border tw-border-red-600': !receivedData.is_active,
            }"
          >
            {{
              receivedData.is_active
                ? t('customer_management.titles.active')
                : t('customer_management.titles.archived')
            }}
          </Badge>
        </div>
        <div className="tw-flex tw-items-center tw-gap-4">
          <Button
            class="gap-2 tw-bg-secondary-500"
            @click="editCustomer"
            v-tooltip="
              customerStore.shouldDisableCustomerActions()
                ? t('common.message.customer_archived_tooltip')
                : null
            "
            :class="
              customerStore.shouldDisableCustomerActions()
                ? 'tw-opacity-50 tw-cursor-not-allowed'
                : 'tw-cursor-pointer'
            "
            :disabled="customerStore.shouldDisableCustomerActions()"
          >
            <Edit className="w-4 h-4" />
            {{ t('customer_management.actions.edit') }}
          </Button>
        </div>
      </div>

      <StatsCards
        :orders="receivedData.orders_count || 0"
        :projects="receivedData.active_projects_count || 0"
        :teamMembers="receivedData.team_members_count || 0"
      />

      <SpendingChart :customerId="customerId" />

      <MainData :customer="receivedData" />

      <TeamAssignment :customer="receivedData" :customerId="customerId" />

      <CustomerForms :customer="receivedData" :customerId="customerId" />

      <Legals :customer="receivedData" />

      <DangerZone
        v-if="currentUser?.user_role.includes('Admin')"
        :customer="receivedData"
        @archive-success="getCustomerDataById"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Edit } from 'lucide-vue-next';
import StatsCards from './StatsCard.vue';
import SpendingChart from './SpendingChart.vue';
import MainData from './MainData.vue';
import TeamAssignment from './TeamAssignment.vue';
import CustomerForms from './CustomerForms.vue';
import Legals from './Legals.vue';
import DangerZone from './DangerZone.vue';
import { ref, reactive, onMounted, type Ref } from 'vue';
import { useRoute } from 'vue-router';
import router from '@/router';

import { useCustomerStore } from '@/stores/customer.store';
import { useUserStore } from '@/stores/user.store';
import { storeToRefs } from 'pinia';
import { useBreadcrumbs } from '@/composables/useBreadcrumbs';
const { setBreadcrumbs } = useBreadcrumbs();
const { n, t } = useI18n();

const { getMainDataById } = useCustomerStore();
const customerStore = useCustomerStore();

const { currentUser } = storeToRefs(useUserStore());

const route = useRoute();
const customerId: Ref<number> = ref(Number(route.params.id));

const receivedData: any = reactive({
  id: customerId.value,
  name: '',
  legal_name: '',
  email: '',
  phone_number: '',
  address: '',
  avatar: '',
  active_projects_count: '',
  active_projects_percentage: '',
  financial_limit_frequency: '',
  orders_count: '',
  orders_percentage: '',
  team_members_count: '',
  team_members_percentage: '',
  legal_notes: '',
  terms_and_conditions: '',
  is_archived: false,
  is_active: true,
});

const isLoading = ref(true);
const frequencyOptions = [
  { label: 'Weekly', value: 'WEEKLY' },
  { label: 'Monthly', value: 'MONTHLY' },
  { label: 'Yearly', value: 'YEARLY' },
  { label: 'All Time', value: 'ALL_TIME' },
];

const getCustomerDataById = async () => {
  try {
    let data = await getMainDataById(customerId.value);
    Object.keys(receivedData).forEach(receivedDataItem => {
      Object.keys(data).forEach(responseItem => {
        if (receivedDataItem == responseItem) {
          receivedData[receivedDataItem] = data[responseItem];
        }
      });
    });
    receivedData.id = data.id || customerId.value;
    receivedData.created_on = new Date(data.created_on).toLocaleString();
    receivedData.customer_limit = n(+data.customer_limit, 'currency');
    receivedData.auto_approval_limit = n(+data.auto_approval_limit, 'currency');
    receivedData.is_active = data.is_active;
    receivedData.financial_limit_frequency = frequencyOptions.find(
      option => option.value === data.financial_limit_frequency
    )?.label;
  } catch (e) {
    console.log(e);
  }
};

const editCustomer = () => {
  router.push({
    name: 'edit-customer',
    params: { id: customerId.value.toString() },
  });
};

onMounted(async () => {
  try {
    // Load main customer data
    await getCustomerDataById();

    setBreadcrumbs([
      {
        labelKey: 'customer_management.titles.customers',
        to: { name: 'customers' },
      },
      {
        label: receivedData.name || t('customer_management.titles.customer_info'),
        // No 'to' for the current page (last item)
      },
    ]);
  } catch (error) {
    console.error('Error loading customer data:', error);
  } finally {
    isLoading.value = false;
  }
});
</script>
